import type { SandboxLogicPlugin } from '../../core/types/plugin.base.types';
import type {
  ExploraPayload,
  ExploraRuntime,
  ExploraVariable,
  InflationMarketPayload,
  SandboxPayload,
  SandboxProjection,
  SandboxRuntime,
} from '../../core/types/payloads/sandbox.types';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant, ScorePatch } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import { formatParts } from './fractionUtils';
import {
  applyOperationToSide,
  defaultInverseDemo,
  defaultInverseOperations,
  formatBalanzaEquation,
  isEquationIsolated,
  isInverseOperationsPayload,
} from './balanzaEquation';
import { validateSimulationMatrixPayload } from '../../core/schemas/mathBlocks';

function isExplora(payload: SandboxPayload): payload is ExploraPayload {
  return payload.variant === 'balanza' || payload.variant === 'fracciones' || payload.variant === 'grafico';
}

function resolveMaxValue(payload: ExploraPayload): number {
  return payload.maxValue ?? Math.max(payload.targetValue * 2, 20);
}

function parseVariable(raw: unknown, index: number): ExploraVariable {
  const v = raw as Record<string, unknown>;
  const rawType = String(v.type || 'ADD');
  const bothTypes = ['BOTH_ADD', 'BOTH_SUBTRACT', 'BOTH_MULTIPLY', 'BOTH_DIVIDE'] as const;
  const type =
    rawType === 'MULTIPLY'
      ? 'MULTIPLY'
      : bothTypes.includes(rawType as (typeof bothTypes)[number])
        ? (rawType as ExploraVariable['type'])
        : 'ADD';
  return {
    id: String(v.id || `var_${index + 1}`),
    label: String(v.label || `Acción ${index + 1}`),
    internalValue: Number(v.internalValue),
    type,
  };
}

function migrateLegacyVariables(raw: Record<string, unknown>, variant: ExploraPayload['variant']): ExploraVariable[] {
  if (Array.isArray(raw.availableVariables) && raw.availableVariables.length > 0) {
    return raw.availableVariables.map(parseVariable);
  }

  if (Array.isArray(raw.steps) && raw.steps.length > 0) {
    return raw.steps.map((s, i) => {
      const step = s as Record<string, unknown>;
      const parts = Number(step.parts);
      return {
        id: String(step.id || `step_${i + 1}`),
        label: String(step.label || `Paso ${i + 1}`),
        internalValue: parts,
        type: 'ADD' as const,
      };
    });
  }

  const step = Number(raw.stepValue ?? 1);
  const unit = raw.unitLabel ? String(raw.unitLabel) : variant === 'balanza' ? 'kg' : '';
  const suffix = unit ? ` ${unit}` : '';
  return [
    { id: 'add', label: `+${step}${suffix}`, internalValue: step, type: 'ADD' },
    { id: 'sub', label: `−${step}${suffix}`, internalValue: -step, type: 'ADD' },
  ];
}

function validateExplora(raw: Record<string, unknown>, variant: ExploraPayload['variant']): ExploraPayload {
  const availableVariables = migrateLegacyVariables(raw, variant);
  if (availableVariables.length < 1) throw new Error('Explora requires at least one variable action');

  const balanceMode =
    raw.balanceMode === 'inverse-operations' ? 'inverse-operations' : 'reach-target';

  const equationLeftRaw = raw.equationLeft as Record<string, unknown> | undefined;
  const equationRightRaw = raw.equationRight as Record<string, unknown> | undefined;
  const equationLeft =
    equationLeftRaw && Number.isFinite(Number(equationLeftRaw.xBoxes))
      ? { xBoxes: Number(equationLeftRaw.xBoxes), constants: Number(equationLeftRaw.constants ?? 0) }
      : undefined;
  const equationRight =
    equationRightRaw && Number.isFinite(Number(equationRightRaw.constants))
      ? { xBoxes: Number(equationRightRaw.xBoxes ?? 0), constants: Number(equationRightRaw.constants) }
      : undefined;

  const inverseMode =
    balanceMode === 'inverse-operations' || Boolean(equationLeft && equationRight && raw.skin === 'algebra');

  if (inverseMode && (!equationLeft || !equationRight)) {
    throw new Error('inverse-operations requires equationLeft and equationRight');
  }

  const targetValue = inverseMode ? equationRight!.constants : Number(raw.targetValue);
  const initialValue = inverseMode ? equationRight!.constants : Number(raw.initialValue ?? 0);

  if (!Number.isFinite(targetValue)) throw new Error('Explora requires a valid targetValue');
  if (!Number.isFinite(initialValue)) throw new Error('Explora requires a valid initialValue');

  const maxValue = raw.maxValue != null ? Number(raw.maxValue) : undefined;
  if (maxValue != null && (!Number.isFinite(maxValue) || maxValue <= 0)) {
    throw new Error('Explora maxValue must be a positive number');
  }

  const deckMode =
    raw.deckMode != null
      ? Boolean(raw.deckMode)
      : variant === 'fracciones' || variant === 'grafico';

  for (const v of availableVariables) {
    const bothOps = ['BOTH_ADD', 'BOTH_SUBTRACT', 'BOTH_MULTIPLY', 'BOTH_DIVIDE'];
    if (bothOps.includes(v.type) && !Number.isFinite(v.internalValue)) {
      throw new Error(`Operation "${v.label}" needs a numeric operand`);
    }
    if (!bothOps.includes(v.type) && !Number.isFinite(v.internalValue)) {
      throw new Error(`Variable "${v.label}" needs a numeric internalValue`);
    }
  }

  return {
    variant,
    title: String(raw.title || (variant === 'balanza' ? 'La balanza' : variant === 'fracciones' ? 'Fracciones en vivo' : 'Simulador gráfico')),
    targetValue: inverseMode ? equationRight!.constants : targetValue,
    initialValue: inverseMode ? equationRight!.constants : initialValue,
    displayEquation: raw.displayEquation
      ? String(raw.displayEquation)
      : inverseMode && equationLeft && equationRight
        ? formatBalanzaEquation(equationLeft, equationRight)
        : undefined,
    balanceMode: inverseMode ? 'inverse-operations' : balanceMode,
    equationLeft,
    equationRight,
    simulationMatrix: raw.simulationMatrix
      ? validateSimulationMatrixPayload(raw.simulationMatrix)
      : undefined,
    availableVariables,
    maxValue,
    unitLabel: raw.unitLabel ? String(raw.unitLabel) : variant === 'balanza' ? 'kg' : undefined,
    deckMode: inverseMode ? false : deckMode,
    deckSize: Number(raw.deckSize) || 3,
    deckDurationSec: Number(raw.deckDurationSec) || 10,
    skin: raw.skin && ['physics', 'algebra', 'economy', 'fractions'].includes(String(raw.skin))
      ? (String(raw.skin) as ExploraPayload['skin'])
      : undefined,
  };
}

export function validateSandboxPayload(raw: unknown): SandboxPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Sandbox payload must be an object');
  const data = raw as Record<string, unknown>;
  const variant = String(data.variant || 'balanza');

  if (variant === 'balanza' || variant === 'fracciones' || variant === 'grafico') {
    return validateExplora(data, variant);
  }

  if (variant === 'inflation-market') {
    const p = data as Partial<InflationMarketPayload>;
    if (!p.productName) throw new Error('Inflation market requires productName');
    if (!Number.isFinite(Number(p.basePrice))) throw new Error('Inflation market requires basePrice');
    if (!Number.isFinite(Number(p.targetPrice))) throw new Error('Inflation market requires targetPrice');
    if (!Array.isArray(p.teams) || p.teams.length < 2) {
      throw new Error('Inflation market requires at least 2 teams');
    }
    return {
      variant: 'inflation-market',
      title: String(p.title || 'Mercado inflacionario'),
      productName: String(p.productName),
      basePrice: Number(p.basePrice),
      targetPrice: Number(p.targetPrice),
      roundDurationSec: Number(p.roundDurationSec) || 60,
      teams: p.teams.map((t, i) => ({
        teamId: String(t.teamId || `team_${i + 1}`),
        label: String(t.label || `Equipo ${i + 1}`),
        modifiers: Array.isArray(t.modifiers)
          ? t.modifiers.map((m) => ({
              id: String(m.id),
              label: String(m.label),
              percent: Number(m.percent),
            }))
          : [],
      })),
      currencySymbol: p.currencySymbol ? String(p.currencySymbol) : '$',
    };
  }

  throw new Error('Sandbox variant must be balanza, fracciones, grafico or inflation-market');
}

function pickDeck(payload: ExploraPayload): ExploraVariable[] {
  const pool = [...payload.availableVariables];
  const size = Math.min(payload.deckSize ?? 3, pool.length);
  const picked: ExploraVariable[] = [];
  while (picked.length < size && pool.length > 0) {
    const idx = Math.floor(Math.random() * pool.length);
    picked.push(pool.splice(idx, 1)[0]);
  }
  return picked;
}

function withDeck(payload: ExploraPayload, runtime: ExploraRuntime): ExploraRuntime {
  if (!payload.deckMode) {
    return { ...runtime, currentDeck: undefined, deckExpiresAt: undefined };
  }
  const now = Date.now();
  return {
    ...runtime,
    currentDeck: pickDeck(payload),
    deckExpiresAt: now + (payload.deckDurationSec ?? 10) * 1000,
  };
}

export function createInitialSandboxRuntime(payload: SandboxPayload): SandboxRuntime {
  if (isExplora(payload)) {
    const inverse = isInverseOperationsPayload(payload);
    const equationLeft = inverse
      ? payload.equationLeft!
      : undefined;
    const equationRight = inverse
      ? payload.equationRight!
      : undefined;
    const currentEquation =
      inverse && equationLeft && equationRight
        ? formatBalanzaEquation(equationLeft, equationRight)
        : undefined;

    const runtime: ExploraRuntime = {
      kind: 'explora',
      currentValue: inverse ? equationRight!.constants : payload.initialValue,
      history: [],
      status: inverse
        ? isEquationIsolated(equationLeft!, equationRight!)
          ? 'SUCCESS'
          : 'PLAYING'
        : payload.initialValue === payload.targetValue
          ? 'SUCCESS'
          : 'PLAYING',
      equationLeft,
      equationRight,
      currentEquation,
    };
    return inverse ? runtime : withDeck(payload, runtime);
  }
  return {
    kind: 'inflation-market',
    currentPrice: payload.basePrice,
    history: [],
  };
}

function clampValue(value: number, payload: ExploraPayload): number {
  const max = resolveMaxValue(payload);
  return Math.max(0, Math.min(max, value));
}

function applyVariableValue(current: number, variable: ExploraVariable): number {
  if (variable.type === 'MULTIPLY') {
    return Math.round(current * variable.internalValue);
  }
  return current + variable.internalValue;
}

function resolveStatus(currentValue: number, payload: ExploraPayload): ExploraRuntime['status'] {
  if (isInverseOperationsPayload(payload)) return 'PLAYING';
  if (currentValue === payload.targetValue) return 'SUCCESS';
  return 'PLAYING';
}

function applyInverseOperation(
  runtime: ExploraRuntime,
  payload: ExploraPayload,
  variable: ExploraVariable,
  participant: Participant,
): ExploraRuntime {
  const op = variable.type;
  if (!['BOTH_ADD', 'BOTH_SUBTRACT', 'BOTH_MULTIPLY', 'BOTH_DIVIDE'].includes(op)) {
    return runtime;
  }

  const left = runtime.equationLeft ?? payload.equationLeft;
  const right = runtime.equationRight ?? payload.equationRight;
  if (!left || !right) return runtime;

  const nextLeft = applyOperationToSide(left, op, variable.internalValue);
  const nextRight = applyOperationToSide(right, op, variable.internalValue);
  if (!nextLeft || !nextRight) return runtime;

  const currentEquation = formatBalanzaEquation(nextLeft, nextRight);
  const historyEntry = {
    timestamp: Date.now(),
    participantName: participant.displayName,
    variableId: variable.id,
    variableLabel: variable.label,
  };

  const isolated = isEquationIsolated(nextLeft, nextRight);
  return {
    ...runtime,
    equationLeft: nextLeft,
    equationRight: nextRight,
    currentEquation,
    currentValue: nextRight.constants,
    history: [...runtime.history, historyEntry],
    status: isolated ? 'SUCCESS' : 'PLAYING',
  };
}

function applyExploraVariable(
  runtime: ExploraRuntime,
  payload: ExploraPayload,
  variable: ExploraVariable,
  participant: Participant,
): ExploraRuntime {
  const nextRaw = applyVariableValue(runtime.currentValue, variable);
  const nextValue = clampValue(nextRaw, payload);
  const historyEntry = {
    timestamp: Date.now(),
    participantName: participant.displayName,
    variableId: variable.id,
    variableLabel: variable.label,
  };
  const next: ExploraRuntime = {
    ...runtime,
    currentValue: nextValue,
    history: [...runtime.history, historyEntry],
    status: resolveStatus(nextValue, payload),
  };
  return withDeck(payload, next);
}

function exploraProjection(ctx: PluginContext<'sandbox'>): Extract<SandboxProjection, { variant: ExploraPayload['variant'] }> {
  const payload = ctx.payload as ExploraPayload;
  const runtime = ctx.runtime as ExploraRuntime;
  const inverse = isInverseOperationsPayload(payload);
  const equationLeft = runtime.equationLeft ?? payload.equationLeft;
  const equationRight = runtime.equationRight ?? payload.equationRight;
  const liveEquation =
    runtime.currentEquation ??
    (equationLeft && equationRight ? formatBalanzaEquation(equationLeft, equationRight) : payload.displayEquation);

  const maxValue = resolveMaxValue(payload);
  const deckTimeRemainingSec =
    runtime.deckExpiresAt != null
      ? Math.max(0, Math.ceil((runtime.deckExpiresAt - Date.now()) / 1000))
      : undefined;

  if (inverse && equationLeft && equationRight) {
    return {
      variant: payload.variant,
      title: payload.title,
      currentValue: equationRight.constants,
      targetValue: equationRight.constants,
      initialValue: payload.initialValue,
      maxValue,
      difference: 0,
      status: runtime.status,
      displayEquation: liveEquation,
      liveEquation,
      unitLabel: payload.unitLabel,
      history: runtime.history,
      currentDeck: runtime.currentDeck,
      deckTimeRemainingSec,
      blockLabels: [],
      inverseMode: true,
      equationLeft,
      equationRight,
      currentFractionLabel: formatParts(equationRight.constants, maxValue),
      targetFractionLabel: formatParts(equationRight.constants, maxValue),
    };
  }

  return {
    variant: payload.variant,
    title: payload.title,
    currentValue: runtime.currentValue,
    targetValue: payload.targetValue,
    initialValue: payload.initialValue,
    maxValue,
    difference: runtime.currentValue - payload.targetValue,
    status: runtime.status,
    displayEquation: payload.displayEquation,
    unitLabel: payload.unitLabel,
    history: runtime.history,
    currentDeck: runtime.currentDeck,
    deckTimeRemainingSec,
    blockLabels: runtime.history.map((h) => h.variableLabel),
    currentFractionLabel: formatParts(runtime.currentValue, maxValue),
    targetFractionLabel: formatParts(payload.targetValue, maxValue),
  };
}

function applyInflationModifier(price: number, percent: number): number {
  return Math.round(price * (1 + percent / 100));
}

function inflationProjection(ctx: PluginContext<'sandbox'>): SandboxProjection {
  const payload = ctx.payload as InflationMarketPayload;
  const runtime = ctx.runtime as Extract<SandboxRuntime, { kind: 'inflation-market' }>;
  const timeRemainingSec = runtime.deadlineAt
    ? Math.max(0, Math.ceil((runtime.deadlineAt - Date.now()) / 1000))
    : undefined;
  return {
    variant: 'inflation-market',
    currentPrice: runtime.currentPrice,
    targetPrice: payload.targetPrice,
    productName: payload.productName,
    history: runtime.history,
    timeRemainingSec,
  };
}

function exploraSuccessScores(
  prev: ExploraRuntime,
  next: ExploraRuntime,
  participant: Participant,
): ScorePatch[] | undefined {
  if (prev.status !== 'PLAYING' || next.status !== 'SUCCESS') return undefined;
  const steps = next.history.length;
  const efficiencyBonus = steps <= 5 ? 1 : 0;
  return [{
    participantId: participant.participantId,
    teamId: participant.teamId,
    delta: 1 + efficiencyBonus,
    reason: 'explora-success',
  }];
}

function mutationWithExploraScores(
  prev: ExploraRuntime,
  next: ExploraRuntime,
  participant: Participant,
): { runtime: ExploraRuntime; scores?: ScorePatch[] } {
  return {
    runtime: next,
    scores: exploraSuccessScores(prev, next, participant),
  };
}

function checkInflationWin(ctx: PluginContext<'sandbox'>) {
  const payload = ctx.payload as InflationMarketPayload;
  const runtime = ctx.runtime as Extract<SandboxRuntime, { kind: 'inflation-market' }>;
  const expired = runtime.deadlineAt ? Date.now() >= runtime.deadlineAt : false;
  const exact = runtime.currentPrice === payload.targetPrice;
  if (!expired && !exact) return { resolved: false };

  let closestTeamId: string | undefined;
  let closestDiff = Infinity;
  for (const team of payload.teams) {
    const lastTeamAction = [...runtime.history].reverse().find((h) => h.teamId === team.teamId);
    if (lastTeamAction) {
      const diff = Math.abs(runtime.currentPrice - payload.targetPrice);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestTeamId = team.teamId;
      }
    }
  }

  return {
    resolved: true,
    winnerTeamId: exact ? runtime.history[runtime.history.length - 1]?.teamId : undefined,
    closestTeamId,
  };
}

function findVariable(payload: ExploraPayload, runtime: ExploraRuntime, variableId: string): ExploraVariable | undefined {
  if (payload.deckMode && runtime.currentDeck?.length) {
    return runtime.currentDeck.find((v) => v.id === variableId);
  }
  return payload.availableVariables.find((v) => v.id === variableId);
}

export const sandboxLogic: SandboxLogicPlugin = {
  mode: 'sandbox',

  validatePayload: validateSandboxPayload,
  createInitialRuntime: createInitialSandboxRuntime,

  getProjection(ctx) {
    if (isExplora(ctx.payload)) return exploraProjection(ctx);
    return inflationProjection(ctx);
  },

  checkWinCondition(ctx) {
    if (isExplora(ctx.payload)) {
      const runtime = ctx.runtime as ExploraRuntime;
      if (runtime.status === 'SUCCESS' || runtime.status === 'FAILED') return { resolved: true };
      return { resolved: false };
    }
    return checkInflationWin(ctx);
  },

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (ctx.phase !== 'ACTIVE') {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
    }

    if (isExplora(ctx.payload)) {
      const payload = ctx.payload;
      let runtime = ctx.runtime as ExploraRuntime;
      if (runtime.status !== 'PLAYING') {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'ROUND_ENDED' };
      }

      if (payload.deckMode && runtime.deckExpiresAt && Date.now() > runtime.deckExpiresAt) {
        runtime = withDeck(payload, runtime);
      }

      if (input.action === 'APPLY_VARIABLE') {
        const data = input.data as { variableId?: string };
        if (!data.variableId) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'MISSING_VARIABLE' };
        }
        const variable = findVariable(payload, runtime, data.variableId);
        if (!variable) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_VARIABLE' };
        }
        if (isInverseOperationsPayload(payload)) {
          const next = applyInverseOperation(runtime, payload, variable, participant);
          if (next === runtime) {
            return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_OPERATION' };
          }
          return mutationWithExploraScores(runtime, next, participant);
        }
        return mutationWithExploraScores(
          runtime,
          applyExploraVariable(runtime, payload, variable, participant),
          participant,
        );
      }

      /** @deprecated Compatibilidad con acciones legacy */
      if (input.action === 'INC' || input.action === 'DEC') {
        const legacy = payload.availableVariables.find((v) =>
          input.action === 'INC' ? v.internalValue > 0 && v.type === 'ADD' : v.internalValue < 0 && v.type === 'ADD',
        );
        if (!legacy) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'LEGACY_ACTION_UNSUPPORTED' };
        }
        return mutationWithExploraScores(
          runtime,
          applyExploraVariable(runtime, payload, legacy, participant),
          participant,
        );
      }

      if (input.action === 'APPLY_STEP') {
        const data = input.data as { stepId?: string };
        const variable = payload.availableVariables.find((v) => v.id === data.stepId);
        if (!variable) {
          return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_STEP' };
        }
        return mutationWithExploraScores(
          runtime,
          applyExploraVariable(runtime, payload, variable, participant),
          participant,
        );
      }

      return { runtime: ctx.runtime };
    }

    if (input.action !== 'APPLY_MODIFIER') return { runtime: ctx.runtime };
    const payload = ctx.payload as InflationMarketPayload;
    const runtime = ctx.runtime as Extract<SandboxRuntime, { kind: 'inflation-market' }>;
    const data = input.data as { modifierId?: string; teamId?: string };
    const teamId = data.teamId || participant.teamId;
    if (!teamId || !data.modifierId) return { runtime: ctx.runtime };

    const team = payload.teams.find((t) => t.teamId === teamId);
    const modifier = team?.modifiers.find((m) => m.id === data.modifierId);
    if (!modifier) return { runtime: ctx.runtime };

    const currentPrice = applyInflationModifier(runtime.currentPrice, modifier.percent);
    return {
      runtime: {
        ...runtime,
        currentPrice,
        history: [
          ...runtime.history,
          { ts: Date.now(), price: currentPrice, teamId, modifierId: modifier.id },
        ],
      },
    };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (isExplora(ctx.payload)) {
      const runtime = ctx.runtime as ExploraRuntime;
      if (action.action === 'RESET') {
        return { runtime: createInitialSandboxRuntime(ctx.payload) };
      }
      if (action.action === 'END_ROUND' && runtime.status === 'PLAYING') {
        return { runtime: { ...runtime, status: 'FAILED' } };
      }
      if (action.action === 'REFRESH_DECK' && ctx.payload.deckMode) {
        return { runtime: withDeck(ctx.payload, runtime) };
      }
      return { runtime: ctx.runtime };
    }

    const payload = ctx.payload as InflationMarketPayload;
    const runtime = ctx.runtime as Extract<SandboxRuntime, { kind: 'inflation-market' }>;

    if (action.action === 'START_ROUND') {
      const now = Date.now();
      return {
        runtime: {
          ...runtime,
          currentPrice: payload.basePrice,
          startedAt: now,
          deadlineAt: now + payload.roundDurationSec * 1000,
          history: [],
          winnerTeamId: undefined,
          closestTeamId: undefined,
        },
      };
    }
    if (action.action === 'END_ROUND') {
      const win = checkInflationWin(ctx);
      return {
        runtime: {
          ...runtime,
          winnerTeamId: win.winnerTeamId,
          closestTeamId: win.closestTeamId,
        },
      };
    }
    return { runtime: ctx.runtime };
  },

  computeScores(ctx, participants): ScorePatch[] {
    if (isExplora(ctx.payload)) {
      const runtime = ctx.runtime as ExploraRuntime;
      if (runtime.status !== 'SUCCESS') return [];
      const actorNames = new Set(runtime.history.map((entry) => entry.participantName));
      return participants
        .filter((p) => actorNames.has(p.displayName))
        .map((p) => ({
          participantId: p.participantId,
          teamId: p.teamId,
          delta: 1,
          reason: 'explora-participation',
        }));
    }

    const payload = ctx.payload as InflationMarketPayload;
    const runtime = ctx.runtime as Extract<SandboxRuntime, { kind: 'inflation-market' }>;
    const winnerTeam = runtime.winnerTeamId ?? runtime.closestTeamId;
    if (!winnerTeam) return [];
    return participants
      .filter((p) => p.teamId === winnerTeam)
      .map((p) => ({
        participantId: p.participantId,
        teamId: p.teamId,
        delta: 2,
        reason: 'inflation-team-win',
      }));
  },
};

export function demoBalanzaPayload(): ExploraPayload {
  return {
    variant: 'balanza',
    title: 'Equilibra la balanza',
    targetValue: 12,
    initialValue: 0,
    maxValue: 20,
    unitLabel: 'kg',
    deckMode: false,
    availableVariables: [
      { id: 'add1', label: '+ 1 kg', internalValue: 1, type: 'ADD' },
      { id: 'sub1', label: '− 1 kg', internalValue: -1, type: 'ADD' },
      { id: 'add3', label: '+ 3 kg', internalValue: 3, type: 'ADD' },
      { id: 'sub2', label: '− 2 kg', internalValue: -2, type: 'ADD' },
    ],
  };
}

export function demoFraccionesPayload(): ExploraPayload {
  return {
    variant: 'fracciones',
    title: 'Llena el tanque',
    targetValue: 5,
    initialValue: 0,
    maxValue: 8,
    deckMode: true,
    deckSize: 3,
    deckDurationSec: 10,
    availableVariables: [
      { id: 'eighth', label: 'Añadir ⅛', internalValue: 1, type: 'ADD' },
      { id: 'quarter', label: 'Añadir ¼', internalValue: 2, type: 'ADD' },
      { id: 'half', label: 'Añadir ½', internalValue: 4, type: 'ADD' },
      { id: 'remove_e', label: 'Quitar ⅛', internalValue: -1, type: 'ADD' },
      { id: 'double', label: 'Duplicar', internalValue: 2, type: 'MULTIPLY' },
      { id: 'halve', label: 'Reducir a la mitad', internalValue: 0.5, type: 'MULTIPLY' },
    ],
  };
}

export function demoInflationMarketPayload(): InflationMarketPayload {
  return {
    variant: 'inflation-market',
    title: 'El mercado inflacionario',
    productName: 'Zapatillas Pro',
    basePrice: 100_000,
    targetPrice: 118_800,
    roundDurationSec: 60,
    currencySymbol: '$',
    teams: [
      {
        teamId: 'team_1',
        label: 'Inflacionistas',
        modifiers: [
          { id: 'up10', label: '+10%', percent: 10 },
          { id: 'up20', label: '+20%', percent: 20 },
        ],
      },
      {
        teamId: 'team_2',
        label: 'Deflacionistas',
        modifiers: [
          { id: 'down10', label: '-10%', percent: -10 },
          { id: 'down15', label: '-15%', percent: -15 },
        ],
      },
    ],
  };
}

export function demoEcuacionesPayload(): ExploraPayload {
  const { equationLeft, equationRight, displayEquation } = defaultInverseDemo();
  return {
    variant: 'balanza',
    title: 'Balanza de Operaciones Inversas',
    targetValue: equationRight.constants,
    initialValue: equationRight.constants,
    maxValue: 14,
    displayEquation,
    balanceMode: 'inverse-operations',
    equationLeft,
    equationRight,
    skin: 'algebra',
    deckMode: false,
    availableVariables: defaultInverseOperations(),
  };
}

export function demoGobernabilidadPayload(): InflationMarketPayload {
  return {
    variant: 'inflation-market',
    title: 'Gobernabilidad en Crisis',
    productName: 'Índice de aprobación pública',
    basePrice: 42,
    targetPrice: 68,
    roundDurationSec: 90,
    currencySymbol: '%',
    teams: [
      {
        teamId: 'team_1',
        label: 'Gabinete social',
        modifiers: [
          { id: 'subsidy', label: 'Subsidio de emergencia (+8%)', percent: 8 },
          { id: 'dialogue', label: 'Diálogo ciudadano (+5%)', percent: 5 },
        ],
      },
      {
        teamId: 'team_2',
        label: 'Equipo fiscal',
        modifiers: [
          { id: 'austerity', label: 'Austeridad fiscal (-6%)', percent: -6 },
          { id: 'invest', label: 'Inversión en infraestructura (+4%)', percent: 4 },
        ],
      },
    ],
  };
}

export function demoSandboxPayload(): SandboxPayload {
  return demoBalanzaPayload();
}
